import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Form, Alert, Spinner, Accordion, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage } from '@fortawesome/free-solid-svg-icons';
import ConfirmationModal from './ConfirmationModal';
import MessageModal from './MessageModal';
import DefectImageInput from './DefectImageInput';
import { DefectReportItem } from '../interfaces';
import axios from 'axios';
import { buildApiUrl } from '../config/api';

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(new Error('קריאת הקובץ נכשלה'));
    r.readAsDataURL(file);
  });
}

interface DefectReportData {
  event_id: number;
  event_name: string;
  equipment_list: DefectReportItem[];
}

interface DefectReportModalProps {
  show: boolean;
  onHide: () => void;
  eventId: number;
  eventName: string;
  onDefectReport: (defectData: any) => void;
}

// פונקציה ליצירת רשימת דיווחי תקלות נפרדים
const createDefectInstancesList = (equipment: DefectReportItem[]) => {
  const defectInstances: any[] = [];
  
  equipment.forEach(item => {
    // אם יש כמות תקולה, נוצר דיווח תקלה
    if (item.quantity_defective && item.quantity_defective > 0) {
      defectInstances.push({
        id: `defect_${item.equipment_id}_${Date.now()}`,
        equipment_id: item.equipment_id,
        equipment_name: item.equipment_name,
        category_name: item.category_name || 'לא מסווג',
        subcategory_name: item.subcategory_name || 'לא מסווג',
        quantity_defective: item.quantity_defective,
        defect_description: item.defect_description || '',
        priority: item.priority || 'medium',
        quantity_needed: item.quantity_needed,
        available_quantity: item.available_quantity,
        current_defective_quantity: item.current_defective_quantity,
        // שמירת הפריט המקורי לכל הפונקציות
        original_item: item
      });
    }
    
    // הוספת דיווחים נפרדים נוספים אם קיימים
    if (item.defect_instances && item.defect_instances.length > 0) {
      item.defect_instances.forEach((instance, index) => {
        if (instance.quantity > 0) {
                  defectInstances.push({
          id: instance.id,
          equipment_id: item.equipment_id,
          equipment_name: item.equipment_name,
          category_name: item.category_name || 'לא מסווג',
          subcategory_name: item.subcategory_name || 'לא מסווג',
          quantity_defective: instance.quantity,
          description: instance.description,
          priority: instance.priority,
          quantity_needed: item.quantity_needed,
          available_quantity: item.available_quantity,
          current_defective_quantity: item.current_defective_quantity,
          original_item: item,
          is_additional_instance: true
        });
        }
      });
    }
  });
  
  return defectInstances;
};

// פונקציה לקבץ דיווחי תקלות לפי קטגוריות ותתי קטגוריות
const groupDefectInstancesByCategory = (defectInstances: any[]) => {
  const grouped: { [category: string]: { [subcategory: string]: any[] } } = {};
  defectInstances.forEach(instance => {
    const category = instance.category_name || 'לא מסווג';
    const subcategory = instance.subcategory_name || 'לא מסווג';
    if (!grouped[category]) grouped[category] = {};
    if (!grouped[category][subcategory]) grouped[category][subcategory] = [];
    grouped[category][subcategory].push(instance);
  });
  return grouped;
};

const DefectReportModal: React.FC<DefectReportModalProps> = ({
  show,
  onHide,
  eventId,
  eventName,
  onDefectReport
}) => {
  const [equipmentList, setEquipmentList] = useState<DefectReportItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalTitle, setConfirmModalTitle] = useState('אישור פעולה');
  const [confirmModalMessage, setConfirmModalMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [completionNotes, setCompletionNotes] = useState('');
  
  // State for MessageModal
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageModalTitle, setMessageModalTitle] = useState('');
  const [messageModalContent, setMessageModalContent] = useState('');
  const [messageModalVariant, setMessageModalVariant] = useState<'success' | 'danger' | 'info' | 'warning'>('info');
  const [closeParentAfterMessage, setCloseParentAfterMessage] = useState(false);

  const priorityOptions = [
    { value: 'low', label: 'נמוך', color: 'text-green-600' },
    { value: 'medium', label: 'בינוני', color: 'text-yellow-600' },
    { value: 'high', label: 'גבוה', color: 'text-orange-600' },
    { value: 'critical', label: 'קריטי', color: 'text-red-600' }
  ];

  useEffect(() => {
    console.log('DefectReportModal useEffect:', { show, eventId });
    if (show && eventId) {
      fetchEquipmentList();
    }
  }, [show, eventId]);

  const fetchEquipmentList = async () => {
    setLoading(true);
    try {
      console.log('Fetching equipment for event:', eventId);
      const response = await axios.get(buildApiUrl(`events/events/${eventId}/get_equipment_for_defect_report/`));
      console.log('Response status:', response.status);
      console.log('Equipment data:', response.data);
      setEquipmentList(response.data.equipment_list);
    } catch (error) {
      console.error('Error fetching equipment list:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (equipmentId: number, quantity: number) => {
    setEquipmentList(prev => 
      prev.map(item => 
        item.equipment_id === equipmentId 
          ? { ...item, quantity_defective: quantity }
          : item
      )
    );
  };

  const handleDescriptionChange = (equipmentId: number, description: string) => {
    setEquipmentList(prev => 
      prev.map(item => 
        item.equipment_id === equipmentId 
          ? { ...item, defect_description: description }
          : item
      )
    );
  };



  const handlePriorityChange = (equipmentId: number, priority: string) => {
    setEquipmentList(prev => 
      prev.map(item => 
        item.equipment_id === equipmentId 
          ? { ...item, priority: priority as any }
          : item
      )
    );
  };



  const actuallyAddDefectInstance = (equipmentId: number) => {
    setEquipmentList(prev => 
      prev.map(item => 
        item.equipment_id === equipmentId 
          ? { 
              ...item, 
              defect_instances: [
                ...(item.defect_instances || []),
                {
                  id: `defect_${Date.now()}_${Math.random()}`,
                  quantity: 1,
                  description: '',
                  priority: 'medium',
                  imageFile: undefined
                }
              ]
            }
          : item
      )
    );
  };

  const addDefectInstance = (equipmentId: number) => {
    const currentItem = equipmentList.find(i => i.equipment_id === equipmentId);
    const existingCount = (currentItem?.quantity_defective && currentItem.quantity_defective > 0 ? 1 : 0) + (currentItem?.defect_instances?.length || 0);
    if (existingCount > 0) {
      setConfirmModalTitle('הוספת דיווח נוסף');
      setConfirmModalMessage('כבר נבחר פריט מסוג זה בדיווח הזה. האם להוסיף עוד?');
      setConfirmAction(() => () => actuallyAddDefectInstance(equipmentId));
      setShowConfirmModal(true);
      return;
    }
    actuallyAddDefectInstance(equipmentId);
  };

  const removeDefectInstance = (equipmentId: number, instanceId: string) => {
    setEquipmentList(prev => 
      prev.map(item => 
        item.equipment_id === equipmentId 
          ? { 
              ...item, 
              defect_instances: (item.defect_instances || []).filter(instance => instance.id !== instanceId)
            }
          : item
      )
    );
  };

  const updateDefectInstance = (equipmentId: number, instanceId: string, field: string, value: any) => {
    setEquipmentList(prev => 
      prev.map(item => 
        item.equipment_id === equipmentId 
          ? { 
              ...item, 
              defect_instances: (item.defect_instances || []).map(instance => 
                instance.id === instanceId 
                  ? { ...instance, [field]: value }
                  : instance
              )
            }
          : item
      )
    );
  };

  // יצירת רשימת דיווחי תקלות נפרדים וקיבוץ לפי קטגוריות
  const defectInstances = useMemo(() => createDefectInstancesList(equipmentList), [equipmentList]);
  const groupedDefectInstances = useMemo(() => groupDefectInstancesByCategory(defectInstances), [defectInstances]);

  const handleSubmit = async () => {
    const defectiveEquipment = equipmentList.filter(item => 
      (item.quantity_defective && item.quantity_defective > 0) ||
      (item.defect_instances && item.defect_instances.length > 0)
    );

    if (defectiveEquipment.length === 0) {
      setMessageModalTitle('שגיאה');
      setMessageModalContent('לא נבחרו פריטים תקולים לדיווח');
      setMessageModalVariant('warning');
      setShowMessageModal(true);
      return;
    }

    setSubmitting(true);
    try {
      const defective_equipment: {
        equipment_id: number;
        quantity_defective: number;
        defect_description: string;
        priority: string;
        image_base64?: string;
      }[] = [];

      for (const item of defectiveEquipment) {
        if (item.quantity_defective && item.quantity_defective > 0) {
          defective_equipment.push({
            equipment_id: item.equipment_id,
            quantity_defective: item.quantity_defective,
            defect_description: item.defect_description || '',
            priority: item.priority || 'medium'
          });
        }
        if (item.defect_instances && item.defect_instances.length > 0) {
          for (const instance of item.defect_instances) {
            if (instance.quantity > 0) {
              const row: (typeof defective_equipment)[0] = {
                equipment_id: item.equipment_id,
                quantity_defective: instance.quantity,
                defect_description: instance.description,
                priority: instance.priority
              };
              if (instance.imageFile) {
                row.image_base64 = await readFileAsDataUrl(instance.imageFile);
              }
              defective_equipment.push(row);
            }
          }
        }
      }

      const defectData = {
        defective_equipment,
        completion_notes: completionNotes
      };

      await onDefectReport(defectData);
      setMessageModalTitle('הצלחה');
      setMessageModalContent('דיווח התקלות נשלח בהצלחה.');
      setMessageModalVariant('success');
      setCloseParentAfterMessage(true);
      setShowMessageModal(true);
    } catch (error: unknown) {
      console.error('Error submitting defect report:', error);
      const msg =
        axios.isAxiosError(error) && error.response?.data && typeof error.response.data === 'object' && 'error' in error.response.data
          ? String((error.response.data as { error: string }).error)
          : 'שגיאה בשליחת דיווח התקלות';
      setMessageModalTitle('שגיאה');
      setMessageModalContent(msg);
      setMessageModalVariant('danger');
      setCloseParentAfterMessage(false);
      setShowMessageModal(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleMessageModalHide = () => {
    setShowMessageModal(false);
    if (closeParentAfterMessage) {
      setCloseParentAfterMessage(false);
      onHide();
    }
  };

  if (!show) return null;

  return (
    <>
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>דיווח תקלות - {eventName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">טוען רשימת ציוד...</p>
          </div>
        ) : (
          <>
            <Alert variant="info" className="mb-3">
              בחר את הפריטים התקולים ודווח על התקלות. השאר שדות ריקים עבור פריטים תקינים.
            </Alert>

            {/* רשימת ציוד של האירוע להוספת דיווחי תקלות חדשים */}
            <div className="mb-4">
              <h5>ציוד האירוע - הוסף דיווחי תקלות:</h5>
              <Accordion defaultActiveKey="0" className="accordion-flush">
                {Object.entries(equipmentList.reduce((acc, item) => {
                  const category = item.category_name || 'לא מסווג';
                  const subcategory = item.subcategory_name || 'לא מסווג';
                  if (!acc[category]) acc[category] = {};
                  if (!acc[category][subcategory]) acc[category][subcategory] = [];
                  acc[category][subcategory].push(item);
                  return acc;
                }, {} as { [category: string]: { [subcategory: string]: DefectReportItem[] } })).map(([category, subcategories], categoryIndex) => (
                  <Accordion.Item eventKey={`equipment_${categoryIndex}`} key={`equipment_${category}`}>
                    <Accordion.Header>
                      <div className="d-flex justify-content-between align-items-center w-100 me-3">
                        <span>
                          <strong>{category}</strong>
                          <Badge bg="primary" className="ms-2">
                            {Object.values(subcategories).flat().length} פריטים
                          </Badge>
                        </span>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>
                      <Accordion defaultActiveKey="0" className="accordion-flush">
                        {Object.entries(subcategories).map(([subcategory, items], subcategoryIndex) => (
                          <Accordion.Item eventKey={`equipment_${categoryIndex}-${subcategoryIndex}`} key={`equipment_${category}-${subcategory}`}>
                            <Accordion.Header>
                              <div className="d-flex justify-content-between align-items-center w-100 me-3">
                                <span>
                                  {subcategory}
                                  <Badge bg="info" className="ms-2">
                                    {items.length} פריטים
                                  </Badge>
                                </span>
                              </div>
                            </Accordion.Header>
                            <Accordion.Body>
                              {items.map((item) => (
                                <div key={item.equipment_id} className="border rounded p-3 mb-3">
                                  <div className="row">
                                    {/* מידע על הפריט */}
                                    <div className="col-md-6">
                                      <h6 className="fw-bold">{item.equipment_name}</h6>
                                      <p className="text-muted mb-1 small">
                                        {item.category_name} - {item.subcategory_name}
                                      </p>
                                      <small className="text-muted">
                                        כמות נדרשת: {item.quantity_needed} | 
                                        כמות זמינה: {item.available_quantity} | 
                                        כמות תקולה נוכחית: {item.current_defective_quantity}
                                      </small>
                                    </div>

                                    {/* כפתור הוספת דיווח */}
                                    <div className="col-md-6 d-flex align-items-center justify-content-end">
                                      {(((item.quantity_defective && item.quantity_defective > 0 ? 1 : 0) + (item.defect_instances?.length || 0)) > 0) && (
                                        <Badge bg="secondary" className="me-2">
                                          נבחר{(((item.quantity_defective && item.quantity_defective > 0 ? 1 : 0) + (item.defect_instances?.length || 0)) > 1) && ` x${(item.quantity_defective && item.quantity_defective > 0 ? 1 : 0) + (item.defect_instances?.length || 0)}`}
                                        </Badge>
                                      )}
                                      <Button 
                                        variant="warning" 
                                        size="sm" 
                                        onClick={() => addDefectInstance(item.equipment_id)}
                                      >
                                        הוסף דיווח תקלה
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </Accordion.Body>
                          </Accordion.Item>
                        ))}
                      </Accordion>
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            </div>

            {/* דיווחי תקלות קיימים */}
            <div className="mb-4">
              <h5>דיווחי תקלות קיימים:</h5>
              <Accordion defaultActiveKey="0" className="accordion-flush">
              {Object.entries(groupedDefectInstances).map(([category, subcategories], categoryIndex) => (
                <Accordion.Item eventKey={categoryIndex.toString()} key={category}>
                  <Accordion.Header>
                    <div className="d-flex justify-content-between align-items-center w-100 me-3">
                      <span>
                        <strong>{category}</strong>
                        <Badge bg="secondary" className="ms-2">
                          {Object.values(subcategories).flat().length} דיווחי תקלות
                        </Badge>
                      </span>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Accordion defaultActiveKey="0" className="accordion-flush">
                      {Object.entries(subcategories).map(([subcategory, items], subcategoryIndex) => (
                        <Accordion.Item eventKey={`${categoryIndex}-${subcategoryIndex}`} key={`${category}-${subcategory}`}>
                          <Accordion.Header>
                            <div className="d-flex justify-content-between align-items-center w-100 me-3">
                              <span>
                                {subcategory}
                                <Badge bg="info" className="ms-2">
                                  {items.length} דיווחי תקלות
                                </Badge>
                              </span>
                            </div>
                          </Accordion.Header>
                          <Accordion.Body>
                            {items.map((instance) => (
              <div key={instance.id} className="border rounded p-3 mb-3 bg-light">
                <div className="row">
                  {/* מידע על הפריט */}
                  <div className="col-md-4">
                    <h6 className="fw-bold">{instance.equipment_name}</h6>
                    <p className="text-muted mb-1 small">
                      {instance.category_name} - {instance.subcategory_name}
                    </p>
                    <small className="text-muted">
                      כמות נדרשת: {instance.quantity_needed} | 
                      כמות זמינה: {instance.available_quantity} | 
                      כמות תקולה נוכחית: {instance.current_defective_quantity}
                    </small>
                  </div>

                  {/* כמות תקולה */}
                  <div className="col-md-3">
                    <Form.Group>
                      <Form.Label className="small fw-medium">כמות תקולה</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        max={instance.quantity_needed}
                        value={instance.quantity_defective || ''}
                        onChange={(e) => updateDefectInstance(instance.equipment_id, instance.id, 'quantity', parseInt(e.target.value) || 0)}
                        placeholder="0"
                        size="sm"
                      />
                    </Form.Group>
                  </div>

                  {/* פריוריטי */}
                  <div className="col-md-3">
                    <Form.Group>
                      <Form.Label className="small fw-medium">פריוריטי</Form.Label>
                      <Form.Select
                        value={instance.priority || 'medium'}
                        onChange={(e) => updateDefectInstance(instance.equipment_id, instance.id, 'priority', e.target.value)}
                        size="sm"
                      >
                        {priorityOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>

                  {/* כפתור מחיקה */}
                  <div className="col-md-2 d-flex align-items-end">
                    <Button 
                      variant="outline-danger" 
                      size="sm" 
                      onClick={() => removeDefectInstance(instance.equipment_id, instance.id)}
                      className="w-100"
                    >
                      ✕ מחק
                    </Button>
                  </div>
                </div>

                {/* תיאור התקלה */}
                <div className="mt-3">
                  <Form.Group>
                    <Form.Label className="small fw-medium">תיאור התקלה</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={instance.description || ''}
                      onChange={(e) => updateDefectInstance(instance.equipment_id, instance.id, 'description', e.target.value)}
                      placeholder="תאר את התקלה..."
                      size="sm"
                    />
                  </Form.Group>
                </div>
                <div className="mt-2">
                  <Form.Group>
                    <Form.Label className="small fw-medium">
                      <FontAwesomeIcon icon={faImage} className="me-1 text-secondary" />
                      תמונה (אופציונלי, עד 10MB)
                    </Form.Label>
                    <DefectImageInput
                      disabled={submitting}
                      selectedFileName={instance.imageFile?.name ?? null}
                      onImageSelected={(f) =>
                        updateDefectInstance(instance.equipment_id, instance.id, 'imageFile', f)
                      }
                      onClear={() =>
                        updateDefectInstance(instance.equipment_id, instance.id, 'imageFile', null)
                      }
                      onValidationError={(msg) => {
                        setMessageModalTitle('שים לב');
                        setMessageModalContent(msg);
                        setMessageModalVariant('warning');
                        setCloseParentAfterMessage(false);
                        setShowMessageModal(true);
                      }}
                    />
                  </Form.Group>
                </div>
              </div>
                            ))}
                          </Accordion.Body>
                        </Accordion.Item>
                      ))}
                    </Accordion>
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>

            {equipmentList.length === 0 && !loading && (
              <Alert variant="warning" className="text-center">
                לא נמצא ציוד לאירוע זה
              </Alert>
            )}
            
            {/* שדה להערות אחרונות */}
            <div className="mt-4">
              <Form.Group>
                <Form.Label className="fw-bold">הערות אחרונות (אופציונלי)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={completionNotes}
                  onChange={(e) => setCompletionNotes(e.target.value)}
                  placeholder="הוסף הערות כלליות על האירוע לאחר סיומו..."
                />
                <Form.Text className="text-muted">
                  הערות אלו יוצגו בחלק "הערות לאחר סיום" בפרטי האירוע
                </Form.Text>
              </Form.Group>
            </div>
            
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={submitting}>
          ביטול
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSubmit} 
          disabled={submitting || loading}
        >
          {submitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              שולח...
            </>
          ) : (
            'שלח דיווח'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
    <ConfirmationModal
      show={showConfirmModal}
      onHide={() => setShowConfirmModal(false)}
      title={confirmModalTitle}
      message={confirmModalMessage}
      onConfirm={() => {
        if (confirmAction) confirmAction();
      }}
      confirmText="הוסף"
      cancelText="ביטול"
      confirmVariant="warning"
    />
    <MessageModal
      show={showMessageModal}
      onHide={handleMessageModalHide}
      title={messageModalTitle}
      message={messageModalContent}
      variant={messageModalVariant}
    />
    </>
  );
};

export default DefectReportModal;